import { RedoIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import {
  Show,
  Button,
  ButtonVariant,
  ButtonSize,
  Hide
} from "shared/components";
import {
  IncomeStatus,
  IncomeType,
  ApplicationStatus,
  VerifyMethodType
} from "shared/types";
import {
  BankStatements,
  EditIncomeModalData,
  EditTransactionsDrawerData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { DateTime } from "luxon";
import { EditIncomeDropdownMenu } from "./EditIncomeDropdownMenu";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded";

interface Props {
  setShowEditIncomeModal: (data: false | EditIncomeModalData) => void;
  setShowEditTransactionsDrawer: (
    data: false | EditTransactionsDrawerData
  ) => void;
  setShowReverifyIncomeModal: (data: number | false) => void;
  income: CombinedIncome;
  applicationStatus: ApplicationStatus;
  applicationId?: string | number;
  currentItem?: VerifiedItem | VerifiedItem[] | undefined;
  isManualApplicant?: boolean;
  allBankStatements?: BankStatements;
}

export const IncomeHeader = ({
  setShowEditIncomeModal,
  setShowEditTransactionsDrawer,
  setShowReverifyIncomeModal,
  income,
  applicationStatus,
  applicationId,
  currentItem,
  isManualApplicant,
  allBankStatements
}: Props) => {
  const isCurrentItemArchived = Array.isArray(currentItem)
    ? currentItem.every((item) => item.is_archived)
    : (currentItem?.is_archived ?? false);
  const { transactions_edited_at, transactions_edited_by_name } = income;
  const isCurrentItemABankStatement =
    Array.isArray(currentItem) &&
    currentItem.every(
      (item) => item.income_source_type === VerifyMethodType.BankStatement
    );
  const shouldShowTransactionsEditedWarning =
    isCurrentItemABankStatement &&
    (transactions_edited_at || transactions_edited_by_name);

  if ([IncomeStatus.Processing, IncomeStatus.Pending].includes(income.status))
    return null;

  return (
    <div className="pb-4 mb-4 space-y-4 border-b border-alpha-40">
      <div className="flex justify-between gap-1 w-full">
        <div className="space-y-1">
          <p className="text-content-1000 font-bold text-lg">
            Income source summary
          </p>
          <p className="text-content-300 font-medium text-sm">
            You can edit or re-verify the income source details
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EditIncomeDropdownMenu
            setShowEditIncomeAmountModal={setShowEditIncomeModal}
            setShowEditIncomeTransactionsDrawer={setShowEditTransactionsDrawer}
            currentItem={currentItem}
            income={income}
            allBankStatements={allBankStatements}
          />
          <Hide
            if={
              income.type === IncomeType.OfferLetter ||
              isCurrentItemArchived ||
              isManualApplicant
            }
          >
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              HugeIcon={RedoIcon}
              disabled={applicationStatus === ApplicationStatus.OnHold}
              tooltip={
                applicationStatus === ApplicationStatus.OnHold ? (
                  <ResumeTooltip applicationId={Number(applicationId || 0)} />
                ) : undefined
              }
              tooltipDelay={250}
              onClick={() => setShowReverifyIncomeModal(income.id)}
            >
              Re-verify
            </Button>
          </Hide>
        </div>
      </div>
      <Show if={shouldShowTransactionsEditedWarning}>
        <Banner
          variant="warning"
          textTheme="base"
          body={
            <>
              Income transactions were edited by{" "}
              <b>
                {transactions_edited_by_name}, on{" "}
                {DateTime.fromISO(transactions_edited_at).toFormat(
                  "MMMM d, yyyy 'at' h:mma"
                )}
              </b>
            </>
          }
          HugeIcon={
            <HugeiconsIcon
              icon={InformationCircleIcon}
              className="shrink-0 text-warning-500"
              size="20"
            />
          }
        />
      </Show>
    </div>
  );
};
